import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { faCheck, faChevronLeft, faPen, faRotateLeft, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject, Subject, Subscription, finalize, forkJoin, of, throwError } from 'rxjs';
import { catchError, debounceTime, first, map, switchMap, tap } from 'rxjs/operators';

import { CanComponentDeactivate, warnOnBrowserUnloadIfUnsaved } from 'src/app/core/guards/unsaved-changes.guard';
import { indicate } from 'src/app/operators';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { CustomAlertDialogComponent } from 'src/app/shared/custom-alert-dialog/custom-alert-dialog.component';
import {
  ComponentPropertyViewModel,
  ComponentType,
  CreateValveRequest,
  EsraPipingAPIClient,
  ReleaseObjectGroupViewModel,
  RevisionStatus,
  ValveViewModel
} from 'src/app/shared/models/autogenerated-piping';
import { ReleaseLifecycleService } from 'src/app/shared/services/release-lifecycle.service';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { getForbiddenMessage } from 'src/app/shared/utils';

export type ValveEditMode = 'clone' | 'create' | 'edit';

export interface ValveEditQueryParams {
  mode: ValveEditMode;
  /** Source valve GUID — required when mode === 'clone' */
  id?: string;
  /** New valve tag name */
  tag?: string;
  businessUnit?: string;
  application?: string;
  /** Valve component type — used in 'create' mode to load property templates */
  type?: string;
}

/**
 * Properties that the backend explicitly excludes on GET /GetValveViewModelById (edit flow).
 * The same names must be filtered out on create/clone so both screens show identical fields.
 * These are either handled by dedicated form controls (application, size, rating, free-field
 * inputs) or are auto-generated by the backend (short description, pipe class description).
 */
const VALVE_DETAIL_PROPERTIES_TO_SKIP: ReadonlySet<string> = new Set([
  'valve_specification_application',
  'valve_pipe_class_description',
  'valve_short_description',
  'rating',
  'size',
  'free_field_property_name',
  'free_field_property_value',
  // description is a top-level ValveViewModel field — the BE does not include it
  // in componentProperties when loading a valve for edit.
  'description',
  // sch_rat is not returned in componentProperties on edit (the edit endpoint
  // only returns the consolidated gasket/seal_gasket property).
  'sch_rat',
]);

@Component({
  selector: 'app-valve-edit-page',
  templateUrl: './valve-edit-page.component.html',
  styleUrls: ['./valve-edit-page.component.scss'],
  standalone: false
})
export class ValveEditPageComponent implements OnInit, OnDestroy, CanComponentDeactivate {

  // ── Icons ──────────────────────────────────────────────────────────────────
  faChevronLeft = faChevronLeft;
  faTrashCan = faTrashCan;
  faPen = faPen;
  faRotateLeft = faRotateLeft;
  faCheck = faCheck;

  // ── State ──────────────────────────────────────────────────────────────────
  mode: ValveEditMode = 'create';
  sourceValveId: string | null = null;
  isLoading$ = new BehaviorSubject<boolean>(false);
  isSaving$ = new BehaviorSubject<boolean>(false);
  /** True while loadAllPropertyOptions() is in-flight */
  isLoadingPropertyOptions$ = new BehaviorSubject<boolean>(false);
  /** True while loadPropertyTemplates() is in-flight (create mode, type selected) */
  isLoadingPropertyTemplate$ = new BehaviorSubject<boolean>(false);
  /** True while short description is being auto-generated */
  isGeneratingShortDescription$ = new BehaviorSubject<boolean>(false);
  /** When true, the short description is manually edited and auto-generation is skipped. */
  isManualShortDescription: boolean = false;
  /** When true, the short description input field is open for active editing. */
  isEditingShortDescription: boolean = false;

  /** Short description value as it was loaded from the server — used by revertShortDescriptionToPrevious(). */
  private loadedShortDescription: string = '';
  /** Manual-override flag as it was loaded from the server — used by revertShortDescriptionToPrevious(). */
  private loadedIsManualShortDescription: boolean = false;

  /**
   * Release ID selected by the user in the `ValveSpecModal` before navigating here.
   * Passed as a query param and forwarded to `CreateValveRequest.releaseId` on save.
   * Only populated in create and clone modes.
   */
  private releaseId: string | null = null;

  /** Property name used by the backend to store the valve short description as a component property.
   *  Excluded from the properties table since short description has its own dedicated form field. */
  private static readonly VALVE_SHORT_DESCRIPTION_PROPERTY_KEY: string = 'valve_short_description';

  /** Emits whenever properties change to trigger auto short-description generation */
  private shortDescriptionTrigger$ = new Subject<void>();

  /**
   * Canonical property display order fetched from getAllComponentProperties for the
   * loaded valve type. Used to sort edit/clone properties in the same sequence as
   * create mode — prevents stale displayOrder values on saved valves causing reordering.
   */
  private canonicalPropertyOrder = new Map<string, number>();

  /** Full source valve — kept for edit mode to preserve all BE-managed fields */
  sourceValve: ValveViewModel | null = null;

  // ── Review workflow state ──────────────────────────────────────────────────
  /**
   * True when the valve's revision is in REVIEW status (1).
   * Determines whether reviewer/owner action buttons are shown.
   */
  isReviewMode: boolean = false;

  /** True when the current user is the author (owner) of this valve's revision. */
  currentUserIsOwner: boolean = false;

  /** True when this valve belongs to a release - action buttons defer to `effectiveRevisionStatus`. */
  isInReleaseContext: boolean = false;

  /**
   * The status that governs action-button visibility. In release context, uses
   * `releaseObjectGroup.reviewStatus` - the release object group's own status, which can
   * differ from the underlying revision status - rather than `fromRevision.status`.
   * Mirrors the equivalent getter in `piping-tabs-parent.component.ts`.
   */
  get effectiveRevisionStatus(): RevisionStatus | undefined {
    if (this.isInReleaseContext) {
      return this.sourceValve?.releaseObjectGroup?.reviewStatus;
    }
    return this.sourceValve?.fromRevision?.status;
  }

  /** True while the Submit for Review API call chain is in-flight. */
  submittingForReview$ = new BehaviorSubject<boolean>(false);

  /** True while the Publish API call is in-flight. */
  publishingIndicator$ = new BehaviorSubject<boolean>(false);

  // ── Compare mode ───────────────────────────────────────────────────────────

  /** Whether compare mode is currently active. */
  isCompareMode: boolean = false;

  /** Previous valve data loaded on first compare toggle. `null` until fetched. */
  previousValveViewModel: ValveViewModel | null = null;

  /** True while the previous valve is being fetched from the API. */
  compareLoadingIndicator$ = new BehaviorSubject<boolean>(false);

  /**
   * Map from property name → value for the previous valve.
   * Rebuilt whenever `previousValveViewModel` is populated.
   */
  previousPropertiesMap: Map<string, string> = new Map();

  /**
   * Properties present in the previous valve but absent from the current form.
   * Shown as deleted rows at the bottom of the properties table in compare mode.
   */
  removedProperties: { name: string; value: string }[] = [];

  /**
   * Whether the compare button should be visible.
   * Requires a previous valve to exist (set when the valve was created by cloning)
   * AND a revision status that is not DRAFT (0), PUBLISHED (3), RETIRED (100), or REJECTED (101).
   * Mirrors the pipe class compare button visibility rules in `piping-tabs-parent`.
   */
  get canShowCompareButton(): boolean {
    if (!this.sourceValve?.previousValve) { return false; }
    const status: number | undefined = this.sourceValve?.fromRevision?.status;
    if (status === undefined || status === null) { return false; }
    // Show for REVIEW (1), APPROVAL (2), and pseudo-statuses (10, 11, 12)
    return status !== 0 && status !== 3 && status !== 100 && status !== 101;
  }

  /**
   * Toggle compare mode on/off. Fetches the previous valve from the API on first entry
   * and caches it for subsequent toggles.
   */
  toggleCompareMode(): void {
    if (this.isCompareMode) {
      this.isCompareMode = false;
      return;
    }

    const previousId: string | undefined = this.sourceValve?.previousValve;
    if (!previousId) {
      // No previous version — enter compare mode with no data (nothing will highlight)
      this.isCompareMode = true;
      return;
    }

    if (this.previousValveViewModel) {
      // Already fetched — just toggle on; diff was computed on first load
      this.isCompareMode = true;
      return;
    }

    this.subscription.add(
      this.esraPipingAPIClient.getValveViewModelById(previousId)
        .pipe(indicate(this.compareLoadingIndicator$), first())
        .subscribe({
          next: (previousValve: ValveViewModel) => {
            this.previousValveViewModel = previousValve;
            this.computePreviousDiff();
            this.isCompareMode = true;
          },
          error: () => {
            this.snackBarService.showError('Failed to load the previous valve for comparison.');
            // Still enter compare mode so the user is not blocked
            this.isCompareMode = true;
          }
        })
    );
  }

  /**
   * Normalizes a property name for comparison purposes by replacing underscores with
   * spaces and lowercasing. This ensures that `body_type` and `body type` are treated
   * as the same field — matching by the displayed name rather than the raw JSON key.
   */
  private normalizePropertyNameForComparison(name: string): string {
    return (name ?? '').replace(/_/g, ' ').toLowerCase().trim();
  }

  /**
   * Builds `previousPropertiesMap` and `removedProperties` from `previousValveViewModel`.
   * Map keys are normalized display names (underscores → spaces, lowercase) so that
   * `body_type` in one valve matches `body type` in another. Raw values are preserved.
   * Must be called after `previousValveViewModel` is set.
   */
  private computePreviousDiff(): void {
    if (!this.previousValveViewModel) { return; }

    const _previousProps = this.deduplicateProperties(this.previousValveViewModel.componentProperties ?? []);

    // Key by normalized display name so `body_type` and `body type` are treated as the same field.
    this.previousPropertiesMap = new Map(
      _previousProps.map(p => [this.normalizePropertyNameForComparison(p.name ?? ''), p.value ?? ''])
    );

    // Determine which previous properties are absent from the current form (by normalized name).
    const _currentNormalizedNames = new Set(
      this.propertiesArray.controls.map(c => this.normalizePropertyNameForComparison(c.get('name')?.value ?? ''))
    );
    this.removedProperties = _previousProps
      .filter(p => !_currentNormalizedNames.has(this.normalizePropertyNameForComparison(p.name ?? '')))
      .map(p => ({ name: p.name ?? '', value: p.value ?? '' }));
  }

  /**
   * Returns the previous valve's value for a given component property name.
   * The name is normalized before lookup so `body_type` matches `body type`.
   *
   * Three-state return:
   * - `undefined`  — previous valve not yet fetched; `RevisionFieldComponent` suppresses diff highlights.
   * - `null`       — previous valve IS loaded but did not have this property; field is treated as new (shown in green).
   * - `string`     — the previous valve had this property; shown as a diff if the value changed.
   *
   * This distinction is critical: returning `undefined` for absent properties would cause new fields
   * to render without any highlight, making them indistinguishable from unchanged fields.
   */
  getPreviousPropertyValue(name: string): string | null | undefined {
    if (!this.previousValveViewModel) { return undefined; }
    const _value: string | undefined = this.previousPropertiesMap.get(this.normalizePropertyNameForComparison(name));
    // Return null (not undefined) when the property is absent — signals "new field" to RevisionFieldComponent.
    // undefined means "not yet loaded"; null means "loaded but absent" (new field, rendered green).
    return _value === undefined ? null : _value;
  }

  /**
   * Returns true when compare mode is active, the previous valve is loaded,
   * and the given property name (normalized) is absent from the previous valve.
   * Used to apply a green row background to newly-added properties.
   */
  isNewProperty(name: string): boolean {
    return this.isCompareMode && !!this.previousValveViewModel
      && !this.previousPropertiesMap.has(this.normalizePropertyNameForComparison(name));
  }

  // ── Dropdown options ───────────────────────────────────────────────────────
  businessUnitOptions: string[] = [];
  applicationOptions: string[] = [];
  valveTypeOptions: string[] = [];
  valveSubtypeOptions: string[] = [];
  valveSubtypeSearch = '';
  /** Property name options loaded from getAllComponentProperties(..., null) for the selected type */
  allValvePropertyOptions: string[] = [];
  /** Map of property name → distinct ordered values, built alongside allValvePropertyOptions */
  allValvePropertyValueMap = new Map<string, string[]>();

  get filteredValveSubtypeOptions(): string[] {
    const search = this.valveSubtypeSearch?.toLowerCase() ?? '';
    return search
      ? this.valveSubtypeOptions.filter(o => o.toLowerCase().includes(search))
      : this.valveSubtypeOptions;
  }

  // ── Main form ──────────────────────────────────────────────────────────────
  form = new FormGroup({
    tag:          new FormControl<string>('', [Validators.required]),
    businessUnit: new FormControl<string>('', [Validators.required]),
    application:  new FormControl<string>('', [Validators.required]),
    sizeFrom:     new FormControl<string>(''),
    sizeTo:       new FormControl<string>(''),
    type:         new FormControl<string>('', [Validators.required]),
    subType:      new FormControl<string>(''),
    rev:          new FormControl<string>(''),
    shortDescription:      new FormControl<string>(''),
    pipeClassDescription:  new FormControl<string>(''),
    freeFieldPropertyName:  new FormControl<string>(''),
    freeFieldPropertyValue: new FormControl<string>(''),
    properties:   new FormArray<FormGroup>([])
  });

  get propertiesArray(): FormArray<FormGroup> {
    return this.form.get('properties') as FormArray<FormGroup>;
  }

  get freeFieldPartialFill(): boolean {
    const name  = this.form.get('freeFieldPropertyName')?.value;
    const value = this.form.get('freeFieldPropertyValue')?.value;
    return (!!name && !value) || (!name && !!value);
  }

  private readonly subscription = new Subscription();
  private readonly releaseLifecycleService: ReleaseLifecycleService = inject(ReleaseLifecycleService);

  /** Release object group of the loaded valve; undefined outside a release. */
  get releaseObjectGroup(): ReleaseObjectGroupViewModel | undefined {
    return this.sourceValve?.releaseObjectGroup;
  }

  /** True when another tab changed this valve's life cycle after the page loaded; saving is blocked until it is reloaded. */
  get isLifecycleOutdated(): boolean {
    return this.releaseLifecycleService.isOutdated(this.releaseObjectGroup);
  }

  /** Tells the user to reload and returns true when saving must not go ahead. */
  private blockSaveIfLifecycleOutdated(): boolean {
    if (!this.isLifecycleOutdated) { return false; }

    this.dialog.open(CustomAlertDialogComponent, {
      width: '450px',
      data: {
        message: 'The life cycle of this item was changed in another tab. Reload the page before saving.',
        buttonText: 'okay'
      }
    });
    return true;
  }

  /** Tells the release page when a save moved this valve to a different lifecycle state. */
  private announceLifecycleChange(before: ReleaseObjectGroupViewModel | undefined, after: ReleaseObjectGroupViewModel | undefined): void {
    if (after && this.releaseLifecycleService.hasLifecycleChanged(before, after)) {
      this.releaseLifecycleService.announceChange(after);
    }
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private esraPipingAPIClient: EsraPipingAPIClient,
    private snackBarService: SnackBarService,
    private dialog: MatDialog,
    private msalService: MsalService
  ) {
    // Covers the browser-level exit paths (tab close, refresh, typed URL) that the
    // canDeactivate route guard cannot observe. See unsaved-changes.guard.ts.
    warnOnBrowserUnloadIfUnsaved(() => this.hasUnsavedChanges());
  }

  /**
   * Implements `CanComponentDeactivate` for the `unsavedChangesGuard` route guard
   * (registered on the `piping/valve/edit` route). The form is only marked dirty by
   * genuine user input -- programmatic `patchValue`/`setValue` calls used while loading
   * data do not affect this flag.
   * @returns True when the form has edits that have not been saved yet.
   */
  hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    this.mode         = (params.get('mode') as ValveEditMode) || 'create';
    this.sourceValveId = params.get('id');
    // releaseId is supplied by the ValveSpecModal for create and clone modes.
    this.releaseId = params.get('releaseId');

    if (this.mode === 'edit') {
      this.form.get('rev')?.disable();
      // Type cannot be changed after a valve is saved — it is part of the valve's identity.
      this.form.get('type')?.disable();
    }

    this.loadDropdowns();
    this.loadAllPropertyOptions();

    this.subscription.add(
      this.form.get('type')!.valueChanges.subscribe(typeName => {
        this.valveSubtypeOptions = [];
        this.valveSubtypeSearch = '';
        this.form.get('subType')!.setValue('');
        // Do NOT clear allValvePropertyOptions / allValvePropertyValueMap here —
        // those are global options that are type-independent and loaded once.
        if (typeName) {
          this.loadValveSubtypeOptions(typeName);
          // In create mode, auto-populate the properties table with the
          // canonical fields for the selected type so users don't have
          // to add them one-by-one with "+ add property".
          if (this.mode === 'create') {
            this.loadPropertyTemplates(typeName);
          }
        } else if (this.mode === 'create') {
          // Type was cleared — reset the properties section.
          this.propertiesArray.clear();
        }
      })
    );

    // Patch after subscription so valueChanges fires for type coming from URL params
    this.form.patchValue({
      tag:          params.get('tag')          ?? '',
      businessUnit: params.get('businessUnit') ?? '',
      application:  params.get('application')  ?? '',
      type:         params.get('type')         ?? ''
    });

    if (this.mode === 'clone' && this.sourceValveId) {
      this.loadSourceValve(this.sourceValveId);
    } else if (this.mode === 'edit' && this.sourceValveId) {
      this.loadSourceValve(this.sourceValveId);
    } else if (this.mode === 'create') {
      const type = params.get('type');
      if (type) {
        // this.loadPropertyTemplates(type); for first version we shouldn't list properties as template
      }
    }

    this.setupShortDescriptionAutoGenerate();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  // ── Data loading ───────────────────────────────────────────────────────────

  private loadDropdowns(): void {
    this.subscription.add(
      forkJoin({
        businessUnits: this.esraPipingAPIClient.getAllBusinessUnits().pipe(catchError(() => of([]))),
        applications:  this.esraPipingAPIClient.getAllValveApplications().pipe(catchError(() => of([]))),
        valveTypes:    this.esraPipingAPIClient.getComponentTypes(false, true).pipe(catchError(() => of([])))
      })
      .pipe(first())
      .subscribe(({ businessUnits, applications, valveTypes }) => {
        this.businessUnitOptions = businessUnits;
        this.applicationOptions  = applications;
        this.valveTypeOptions    = valveTypes;
      })
    );
  }

  private loadSourceValve(valveId: string): void {
    this.subscription.add(
      this.esraPipingAPIClient.getValveViewModelById(valveId)
        .pipe(
          indicate(this.isLoading$),
          first(),
          // Chain-fetch the canonical property order for this type so that
          // edit/clone mode sorts properties in the same sequence as create mode.
          switchMap((valve: ValveViewModel) => {
            const typeName: string = valve.componentType?.name ?? '';
            if (!typeName) {
              return of({ valve, canonicalProps: [] as ComponentPropertyViewModel[] });
            }
            return this.esraPipingAPIClient.getAllComponentProperties('Valves', typeName, false, true, undefined)
              .pipe(
                first(),
                catchError(() => of([] as ComponentPropertyViewModel[])),
                map((canonicalProps: ComponentPropertyViewModel[]) => ({ valve, canonicalProps }))
              );
          }),
          tap(({ valve, canonicalProps }) => {
            const orderMap = new Map<string, number>();
            canonicalProps.forEach(p => {
              if (!p.name) { return; }
              const existing = orderMap.get(p.name);
              if (existing === undefined || (p.displayOrder ?? 0) < existing) {
                orderMap.set(p.name, p.displayOrder ?? 0);
              }
            });
            this.canonicalPropertyOrder = orderMap;
            this.populateFromValve(valve);
          })
        )
        .subscribe({
          error: (error: unknown) => this.snackBarService.showError(getForbiddenMessage(error, 'Failed to load source valve data.'))
        })
    );
  }

  private loadValveSubtypeOptions(typeName: string, restoreSubType?: string): void {
    this.subscription.add(
      this.esraPipingAPIClient.getAllComponentProperties("Valves", typeName, false, true, "subType_subType")
        .pipe(indicate(this.isLoading$), first())
        .subscribe({
          next: (props) => {
            this.valveSubtypeOptions = props
              .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
              .map(p => p.value ?? '');
            if (restoreSubType && this.valveSubtypeOptions.includes(restoreSubType)) {
              this.form.get('subType')!.setValue(restoreSubType);
            }
          },
          error: () => this.snackBarService.showError('Failed to load subtype options for this valve type.')
        })
    );
  }

  private loadAllPropertyOptions(): void {
    this.subscription.add(
      this.esraPipingAPIClient.getAllValvesComponentProperties()
        .pipe(indicate(this.isLoadingPropertyOptions$), first())
        .subscribe({
          next: (props) => {
            const namesSeen = new Set<string>();
            const valueMap = new Map<string, string[]>();

            props.forEach(p => {
              // Exclude meta-properties that are handled by dedicated form controls
              // or auto-generated by the backend — mirrors the edit-mode BE filter.
              if (!p.name || VALVE_DETAIL_PROPERTIES_TO_SKIP.has(p.name)) { return; }
              if (!namesSeen.has(p.name)) {
                namesSeen.add(p.name);
              }
              if (p.value) {
                if (!valueMap.has(p.name)) { valueMap.set(p.name, []); }
                const vals = valueMap.get(p.name)!;
                if (!vals.includes(p.value)) { vals.push(p.value); }
              }
            });

            this.allValvePropertyOptions = Array.from(namesSeen);
            this.allValvePropertyValueMap = valueMap;

            // If the source valve loaded before property options were ready,
            // rebuild the form array now so value dropdowns can resolve correctly.
            if (this.sourceValve) {
              const deduped = this.deduplicateProperties(this.sourceValve.componentProperties ?? []);
              const sortedProps = deduped.sort((a, b) => {
                const orderA = this.canonicalPropertyOrder.get(a.name ?? '') ?? (a.displayOrder ?? 0);
                const orderB = this.canonicalPropertyOrder.get(b.name ?? '') ?? (b.displayOrder ?? 0);
                return orderA - orderB;
              });
              this.buildPropertiesFormArray(
                sortedProps.map(p => ({
                  id: p.id,
                  name: p.name ?? '',
                  value: p.value ?? '',
                  displayOrder: p.displayOrder ?? 0
                }))
              );
            }
          },
          error: () => this.snackBarService.showError('Failed to load property options for this valve type.')
        })
    );
  }

  /**
   * In create mode: loads the canonical set of component property names defined
   * for the selected valve type and pre-populates the form array with empty rows,
   * one per distinct property name, so the user can fill in values directly.
   */
  private loadPropertyTemplates(typeName: string): void {
    // Clear while the new type's template loads.
    this.propertiesArray.clear();

    this.subscription.add(
      this.esraPipingAPIClient.getAllComponentProperties('Valves', typeName, false, true, undefined)
        .pipe(indicate(this.isLoadingPropertyTemplate$), first())
        .subscribe({
          next: (props) => {
            // Build a map of name → minimum displayOrder to deduplicate across valves.
            // Exclude subType_subType because sub-type has its own top-level form field.
            const nameOrderMap = new Map<string, number>();
            props.forEach(p => {
              // Exclude subType_subType (own top-level field) and all properties that the
              // backend strips on edit — see VALVE_DETAIL_PROPERTIES_TO_SKIP.
              if (!p.name || p.name === 'subType_subType' || VALVE_DETAIL_PROPERTIES_TO_SKIP.has(p.name)) { return; }
              const existing = nameOrderMap.get(p.name);
              if (existing === undefined || (p.displayOrder ?? 0) < existing) {
                nameOrderMap.set(p.name, p.displayOrder ?? 0);
              }
            });

            const sortedEntries = Array.from(nameOrderMap.entries())
              .sort((a, b) => a[1] - b[1]);

            this.buildPropertiesFormArray(
              sortedEntries.map(([name, displayOrder]) => ({ name, value: '', displayOrder })),
              true  // names are fixed by the type template — show as labels, not dropdowns
            );
          },
          error: () => this.snackBarService.showError('Failed to load property template for this valve type.')
        })
    );
  }

  private setupShortDescriptionAutoGenerate(): void {
    // Single subscription on the whole properties array — fires whenever any
    // name or value changes, or a row is added/removed.
    this.subscription.add(
      this.propertiesArray.valueChanges.subscribe(() =>
        this.shortDescriptionTrigger$.next()
      )
    );

    this.subscription.add(
      this.shortDescriptionTrigger$.pipe(
        // Wait until the user has stopped making changes for 1 s before firing.
        // This collapses rapid name→value selections into a single request.
        debounceTime(1500),
        // Only proceed when there is at least one fully-filled property row
        // (name + value both present) to avoid pointless calls.
        switchMap(() => {
          // Skip auto-generation when the user has opted for manual editing
          if (this.isManualShortDescription) { return of(null); }

          const hasCompleteRow = this.propertiesArray.controls.some(
            g => g.get('name')?.value && g.get('value')?.value
          );
          if (!hasCompleteRow) { return of(null); }

          const vm = this.buildValveViewModelForShortDescription();
          return this.esraPipingAPIClient.generateValveShortDescription(vm)
            .pipe(
              indicate(this.isGeneratingShortDescription$),
              catchError(() => of(null))
            );
        })
      ).subscribe(description => {
        if (description != null) {
          this.form.get('shortDescription')!.setValue(description);
        }
      })
    );
  }

  private buildValveViewModelForShortDescription(): ValveViewModel {
    const v = this.form.getRawValue();
    const vm = new ValveViewModel();
    if (v.type) {
      const ct = new ComponentType();
      ct.name = v.type;
      vm.componentType = ct;
    }
    vm.valveSubType        = v.subType || undefined;
    vm.componentProperties = this.buildRequestProperties((v.properties as any[]) ?? []);
    vm.components          = [];

    const propValue = (rawName: string): string | undefined =>
      vm.componentProperties?.find(p => p.name === rawName)?.value || undefined;

    vm.trim  = propValue('trim');
    vm.ends  = propValue('ends');
    vm.body  = propValue('body');
    vm.class = propValue('class_name');

    return vm;
  }

  private populateFromValve(valve: ValveViewModel): void {
    this.sourceValve = valve;

    // Resolve ownership synchronously from the authorOid so canEditFields is correct
    // immediately after the valve loads — before the getRevisionMembers API call returns.
    const currentOid: string | undefined = this.msalService.instance.getActiveAccount()
      ?.idTokenClaims?.['oid'] as string | undefined;
    this.currentUserIsOwner = currentOid === valve.fromRevision?.authorOid;

    // Determine whether the revision is in REVIEW (1) mode.
    const status: RevisionStatus | undefined = valve.fromRevision?.status;
    this.isReviewMode = status === 1;

    // A valve belongs to a release when the backend returns a releaseObjectGroup.
    this.isInReleaseContext = !!valve.releaseObjectGroup;

    // Detect whether the short description was manually overridden.
    this.loadedIsManualShortDescription = valve.isShortDescriptionManuallyOverridden === true;
    this.isManualShortDescription = this.loadedIsManualShortDescription;
    this.loadedShortDescription = valve.shortDescription ?? '';

    // Parse size range — "3/4 - 1-1/2"
    const sizeParts = (valve.size ?? '').split(' - ');

    const typeName = valve.componentType?.name ?? this.form.value.type ?? '';
    const subType  = valve.valveSubType ?? '';

    const patch: any = {
      sizeFrom:             sizeParts[0]?.trim()         ?? '',
      sizeTo:               sizeParts[1]?.trim()         ?? '',
      type:                 typeName,
      subType:              subType,
      shortDescription:      valve.shortDescription      ?? '',
      pipeClassDescription:  valve.pipeClassDescription  ?? '',
      freeFieldPropertyName:  valve.freeFieldPropertyName  ?? '',
      freeFieldPropertyValue: valve.freeFieldPropertyValue ?? ''
    };

    // In edit mode populate all header fields from the loaded valve
    if (this.mode === 'edit') {
      patch.tag          = valve.tag                    ?? '';
      patch.businessUnit = valve.businessUnits?.[0]     ?? '';
      patch.application  = valve.application            ?? '';
      patch.rev          = valve.fromRevision?.version  ?? '';
    }

    this.form.patchValue(patch, { emitEvent: false });

    // Load subtype options; restore the saved subType once options arrive.
    // We bypass valueChanges here (emitEvent: false above) to avoid a double call.
    this.valveSubtypeOptions = [];
    this.valveSubtypeSearch = '';

    if (typeName) {
      this.loadValveSubtypeOptions(typeName, subType || undefined);
    }

    // Sort by canonical order (from getAllComponentProperties) so that the property
    // sequence in edit/clone mode always matches the create mode template order.
    // Deduplicate by name first: some saved valves have multiple entries for the same
    // property (e.g. one with a value and one empty). Keep the entry with a value;
    // if both have values keep the one with the lower stored displayOrder.
    const deduped = this.deduplicateProperties(valve.componentProperties ?? []);
    const sorted = deduped.sort((a, b) => {
      const orderA = this.canonicalPropertyOrder.get(a.name ?? '') ?? (a.displayOrder ?? 0);
      const orderB = this.canonicalPropertyOrder.get(b.name ?? '') ?? (b.displayOrder ?? 0);
      return orderA - orderB;
    });
    this.buildPropertiesFormArray(
      sorted.map(p => ({
        id: p.id,
        name: p.name ?? '',
        value: p.value ?? '',
        displayOrder: p.displayOrder ?? 0
      }))
    );
  }

  /**
   * Deduplicates an array of component properties by name, excluding any properties
   * in VALVE_DETAIL_PROPERTIES_TO_SKIP. When a name appears more than once, the entry
   * with a non-empty value is kept. If multiple entries have values, the one with the
   * lower displayOrder wins.
   */
  private deduplicateProperties(props: ComponentPropertyViewModel[]): ComponentPropertyViewModel[] {
    const byName = new Map<string, ComponentPropertyViewModel>();
    // Sort by displayOrder first so the lower-order entry is seen first for each name.
    const ordered = [...props].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
    for (const p of ordered) {
      const key = p.name ?? '';
      if (VALVE_DETAIL_PROPERTIES_TO_SKIP.has(key)) { continue; }
      const existing = byName.get(key);
      if (!existing) {
        byName.set(key, p);
      } else if (!existing.value && p.value) {
        // Upgrade: replace empty entry with one that has a value.
        byName.set(key, p);
      }
      // If both have values, keep the earlier one (already in the map).
    }
    return Array.from(byName.values());
  }

  private buildPropertiesFormArray(
    items: { id?: string; name: string; value: string; displayOrder: number }[],
    isTemplate: boolean = false
  ): void {
    this.propertiesArray.clear({ emitEvent: false });
    items.forEach(item =>
      this.propertiesArray.push(this.createPropertyGroup(item, isTemplate), { emitEvent: false })
    );
    // Emit a single notification after all rows are inserted so Angular renders
    // the complete table in one pass instead of triggering change detection per row.
    this.propertiesArray.updateValueAndValidity();
  }

  private createPropertyGroup(
    item: { id?: string; name: string; value: string; displayOrder?: number } = { name: '', value: '' },
    isTemplate: boolean = false
  ): FormGroup {
    const propertySelected = !!(item.name ?? '');
    const group = new FormGroup({
      id:           new FormControl(item.id           ?? ''),
      name:         new FormControl(item.name         ?? '', [Validators.required]),
      value:        new FormControl({ value: item.value ?? '', disabled: !propertySelected }),
      displayOrder: new FormControl(item.displayOrder ?? 0),
      // Marks rows pre-populated by loadPropertyTemplates — name is fixed, no dropdown needed.
      isTemplate:   new FormControl(isTemplate)
    });

    this.subscription.add(
      group.get('name')!.valueChanges.subscribe(name => {
        const valueCtrl = group.get('value')!;
        if (name) {
          valueCtrl.enable({ emitEvent: false });
        } else {
          valueCtrl.setValue('', { emitEvent: false });
          valueCtrl.disable({ emitEvent: false });
        }
      })
    );

    return group;
  }

  // ── Property-row actions ───────────────────────────────────────────────────

  addProperty(): void {
    this.propertiesArray.push(this.createPropertyGroup());
    // FormArray.push()/removeAt() are structural mutations -- Angular does not mark the
    // array (or its parent form) dirty for these, only for control value edits driven by
    // UI input. Mark it explicitly so the unsaved-changes guard sees this as a real change.
    this.propertiesArray.markAsDirty();
  }

  removeProperty(index: number): void {
    this.propertiesArray.removeAt(index);
    this.propertiesArray.markAsDirty();
  }

  /** Opens the short description input for manual editing. */
  startManualShortDescriptionEdit(): void {
    this.isManualShortDescription = true;
    this.isEditingShortDescription = true;
  }

  /** Confirms the typed value and collapses the input back to display mode. */
  confirmManualShortDescription(): void {
    this.isEditingShortDescription = false;
  }

  /**
   * Discards any edits and restores the short description to the value
   * that was loaded from the server when the page opened.
   */
  revertShortDescriptionToPrevious(): void {
    this.form.get('shortDescription')?.setValue(this.loadedShortDescription);
    this.isManualShortDescription = this.loadedIsManualShortDescription;
    this.isEditingShortDescription = false;
  }

  replaceUnderscoreWithSpace(value: string): string {
    return (value ?? '').replace(/_/g, ' ');
  }


  displayPropertyName = (value: string): string => this.replaceUnderscoreWithSpace(value ?? '');

  filteredPropertyNameOptions(index: number): string[] {
    const q = (this.propertiesArray.at(index)?.get('name')?.value ?? '').toLowerCase();
    return q
      ? this.allValvePropertyOptions.filter(o =>
          o.toLowerCase().includes(q) ||
          this.replaceUnderscoreWithSpace(o).toLowerCase().includes(q)
        )
      : this.allValvePropertyOptions;
  }

  propertyValueOptions(index: number): string[] {
    const name = this.propertiesArray.at(index)?.get('name')?.value ?? '';
    const key = this.resolvePropertyMapKey(name);
    return this.allValvePropertyValueMap.get(key) ?? [];
  }

  filteredPropertyValueOptions(index: number): string[] {
    const opts = this.propertyValueOptions(index);
    const q = (this.propertiesArray.at(index)?.get('value')?.value ?? '').toLowerCase();
    return q ? opts.filter(o => o.toLowerCase().includes(q)) : opts;
  }

  onSubtypeSearch(event: Event): void {
    this.valveSubtypeSearch = (event.target as HTMLInputElement).value;
  }

  // ── Form submission ────────────────────────────────────────────────────────

  /**
   * True when the form fields should be editable.
   * - Create / clone modes: always editable (no revision status to check).
   * - Edit mode: only the revision owner can edit, and only when the revision is in DRAFT (0).
   */
  get canEditFields(): boolean {
    if (this.mode !== 'edit') { return true; }
    if (!this.sourceValve) { return false; }
    const status: RevisionStatus | undefined = this.sourceValve.fromRevision?.status;
    // Only DRAFT (0) revisions are editable
    if (status !== 0 && status !== undefined) { return false; }
    // A valve locked by its (unpublished) release life cycle is read-only.
    if (this.releaseLifecycleService.isEditLocked(this.releaseObjectGroup)) { return false; }
    return this.currentUserIsOwner;
  }

  /**
   * The numeric revision status from the loaded source valve.
   * Returns `undefined` when no valve / revision is loaded.
   */
  get revisionStatus(): RevisionStatus | undefined {
    return this.sourceValve?.fromRevision?.status;
  }

  /**
   * Returns the revision status as a number for template pipe consumption.
   * Falls back to -1 (renders as 'unknown-status') when not yet loaded.
   */
  get revisionStatusForDisplay(): number {
    return this.sourceValve?.fromRevision?.status ?? -1;
  }

  /**
   * True when the current user can publish this valve revision.
   * Mirrors the pipe class logic: REVIEW (1), APPROVAL (2), or REVIEW_WITH_REJECTIONS (10)
   * and the user is the owner or an approver.
   */
  get canPublishRevision(): boolean {
    const status: RevisionStatus | undefined = this.revisionStatus;
    if (status === undefined || status === null) { return false; }
    const publishableStatuses: number[] = [1, 2, 10, 11, 12];
    if (!publishableStatuses.includes(status)) { return false; }

    const currentOid: string | undefined = this.msalService.instance.getActiveAccount()?.idTokenClaims?.['oid'] as string | undefined;
    const isAuthor: boolean = currentOid === this.sourceValve?.fromRevision?.authorOid;
    return isAuthor;
  }

  isFormValid(): boolean {
    return this.form.valid && !this.isSaving$.getValue();
  }

  onSave(): void {
    if (!this.isFormValid()) {
      return;
    }

    // ── Edit existing valve ─────────────────────────────────────────────────
    if (this.mode === 'edit') {
      if (this.blockSaveIfLifecycleOutdated()) { return; }

      if (this.releaseLifecycleService.requiresReviseOnSave(this.releaseObjectGroup)) {
        this.subscription.add(
          this.releaseLifecycleService.confirmReviseOnSave().pipe(first()).subscribe((confirmed: boolean) => {
            if (confirmed) {
              this.saveEditedValve();
            }
          })
        );
        return;
      }

      this.saveEditedValve();
      return;
    }

    // ── Clone or create ───────────────────────────────────────────────────
    // Always use createValveFromRequest; in clone mode the form is pre-populated
    // with the source valve's properties so they are included in the request.
    this.subscription.add(
      this.esraPipingAPIClient.createValveFromRequest(this.buildCreateRequest())
        .pipe(indicate(this.isSaving$), first())
        .subscribe({
          next: (newValveId: string) => {
            // Mark pristine before navigating so the unsaved-changes guard does not
            // prompt again for the navigation that immediately follows a successful save.
            this.form.markAsPristine();
            const label = this.mode === 'clone' ? 'Valve cloned successfully' : 'Valve created successfully';
            this.snackBarService.showSnackBar(true, label, 'mat-snack-bar-success');
            this.router.navigate(['/valvesummary', newValveId]);
          },
          error: (err) => {
            this.snackBarService.showError(err?.response ?? 'Failed to save valve. Please try again.');
          }
        })
    );
  }

  /** Persists the edited valve and stays on the page with the saved data, like the pipe class editor. */
  private saveEditedValve(): void {
    this.subscription.add(
      this.esraPipingAPIClient.updateValve(this.buildUpdateViewModel())
        .pipe(indicate(this.isSaving$), first())
        .subscribe({
          next: (updated: ValveViewModel) => {
            this.announceLifecycleChange(this.releaseObjectGroup, updated.releaseObjectGroup);
            this.populateFromValve(updated);
            if (this.isCompareMode) {
              this.computePreviousDiff();
            }
            this.form.markAsPristine();
            this.dialog.open(CustomAlertDialogComponent, {
              width: '400px',
              data: { message: 'saved successfully!', buttonText: 'okay' }
            });
          },
          error: (err) => {
            this.snackBarService.showError(err?.response ?? 'Failed to save valve. Please try again.');
          }
        })
    );
  }

  onCancel(): void {
    if (this.mode === 'edit') {
      this.router.navigate(['/piping/revisionsAndReleases']);
    } else {
      this.router.navigate(['/piping/adminSearch']);
    }
  }

  // ── Review workflow ────────────────────────────────────────────────────────

  /**
   * Saves the current valve changes and then advances the revision status to
   * REVIEW (1), notifying the assigned reviewers.
   */
  submitForReview(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '450px',
      data: {
        dialogMessage: 'Do you wish to submit this valve revision for review?',
        confirmText: 'Submit',
        cancelText: 'cancel'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) { return; }
      this.performSaveThenSubmitForReview();
    });
  }

  /**
   * Saves the valve then changes the revision status to REVIEW (1).
   * The spinner stays on for the full chain using explicit indicator management.
   */
  private performSaveThenSubmitForReview(lifecycleConfirmed: boolean = false): void {
    if (!this.isFormValid()) { return; }
    if (this.blockSaveIfLifecycleOutdated()) { return; }

    if (!lifecycleConfirmed && this.releaseLifecycleService.requiresReviseOnSave(this.releaseObjectGroup)) {
      this.subscription.add(
        this.releaseLifecycleService.confirmReviseOnSave().pipe(first()).subscribe((confirmed: boolean) => {
          if (confirmed) {
            this.performSaveThenSubmitForReview(true);
          }
        })
      );
      return;
    }

    this.submittingForReview$.next(true);

    this.esraPipingAPIClient.updateValve(this.buildUpdateViewModel())
      .pipe(
        first(),
        catchError(saveError => {
          this.snackBarService.showError('Failed to save valve. Review submission was cancelled.');
          return throwError(() => saveError);
        }),
        switchMap((updated: ValveViewModel) => {
          // Refresh local state so subsequent edits use the saved data.
          this.announceLifecycleChange(this.releaseObjectGroup, updated.releaseObjectGroup);
          this.sourceValve = updated;
          const revisionId: number | undefined = updated.fromRevision?.id;
          if (!revisionId) {
            return throwError(() => new Error('No revision ID on saved valve.'));
          }
          return this.esraPipingAPIClient.changeRevisionStatus(
            revisionId, RevisionStatus.REVIEW, this.isInReleaseContext ? updated.id : undefined, updated.fromRevision?.description
          ).pipe(
            catchError(statusError => {
              this.snackBarService.showError('Valve saved, but failed to submit for review. Please try again.');
              return throwError(() => statusError);
            })
          );
        }),
        finalize(() => {
          this.submittingForReview$.next(false);
        })
      )
      .subscribe({
        next: () => {
          this.refreshValveStatus();
          this.snackBarService.showSnackBar(
            true,
            'Valve revision submitted for review. Reviewers will be notified.',
            'mat-snack-bar-success'
          );
        },
        error: () => {
          // Errors already reported via snackbar in the pipe operators above.
        }
      });
  }

  /**
   * Returns the revision to DRAFT (0), allowing the owner to make further edits.
   */
  returnToDraft(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        dialogMessage: 'Return this revision to Draft? All reviewer approvals will be reset.',
        confirmText: 'Return to Draft',
        cancelText: 'cancel'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) { return; }
      this.performReturnToDraft();
    });
  }

  private performReturnToDraft(): void {
    const revisionId: number | undefined = this.sourceValve?.fromRevision?.id;
    if (!revisionId) { return; }

    this.subscription.add(
      this.esraPipingAPIClient.changeRevisionStatus(
        revisionId, RevisionStatus.DRAFT, this.isInReleaseContext ? this.sourceValve?.id : undefined, this.sourceValve?.fromRevision?.description
      ).pipe(first())
        .subscribe({
          next: () => {
            this.refreshValveStatus();
            this.snackBarService.showSnackBar(true, 'Revision returned to Draft.', 'mat-snack-bar-success');
          },
          error: () => {
            this.snackBarService.showError('Failed to return revision to Draft. Please try again.');
          }
        })
    );
  }

  /**
   * Silently refetches the valve's revision/release status after submitting for review or
   * returning to draft, instead of hand-mutating local fields. Only `GetValveViewModelById`
   * is called - the rest of the form data is unchanged by a status transition, so there is
   * no need to reload property options or canonical property ordering.
   */
  private refreshValveStatus(): void {
    const valveId: string | undefined = this.sourceValve?.id;
    if (!valveId) { return; }

    this.subscription.add(
      this.esraPipingAPIClient.getValveViewModelById(valveId)
        .pipe(first())
        .subscribe({
          next: (valve: ValveViewModel) => {
            this.sourceValve = valve;
            this.isReviewMode = valve.fromRevision?.status === 1;
            this.isInReleaseContext = !!valve.releaseObjectGroup;
            if (valve.releaseObjectGroup) {
              // The release table's review column follows this status change.
              this.releaseLifecycleService.announceChange(valve.releaseObjectGroup);
            }
          },
          error: () => {
            // Non-critical: the status change itself already succeeded and was confirmed via snackbar.
          }
        })
    );
  }

  /**
   * Publishes the valve revision. Available to the owner or an approver when the
   * revision is in REVIEW (1), APPROVAL (2), or REVIEW_WITH_REJECTIONS (10) status.
   */
  publishValveRevision(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '450px',
      data: {
        dialogMessage: 'Are you sure you want to publish this valve revision? This will make it publicly available.',
        confirmText: 'Publish',
        cancelText: 'cancel'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) { return; }

      const revisionId: number | undefined = this.sourceValve?.fromRevision?.id;
      if (!revisionId) { return; }

      this.subscription.add(
        this.esraPipingAPIClient.publishRevision(revisionId)
          .pipe(indicate(this.publishingIndicator$), first())
          .subscribe({
            next: () => {
              if (this.sourceValve?.fromRevision) {
                this.sourceValve.fromRevision.status = RevisionStatus.PUBLISHED;
              }
              this.isReviewMode = false;
              this.snackBarService.showSnackBar(true, 'Valve revision published successfully.', 'mat-snack-bar-success');
              this.router.navigate(['/valvesummary', this.sourceValve?.id]);
            },
            error: () => {
              this.snackBarService.showError('Failed to publish valve revision. Please try again.');
            }
          })
      );
    });
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  get pageTitle(): string {
    return this.mode === 'clone'
      ? `Clone Valve — ${this.form.value.tag ?? ''}`
      : this.mode === 'edit'
        ? `Edit Valve — ${this.form.value.tag ?? ''}`
        : 'Create New Valve';
  }

  /**
   * Current size as a display string, reconstructed from the form controls.
   * Used in compare mode where the previous valve stores size as a single string.
   */
  get currentSizeDisplay(): string {
    const _from: string = this.form.get('sizeFrom')?.value ?? '';
    const _to: string = this.form.get('sizeTo')?.value ?? '';
    return _from && _to ? `${_from} - ${_to}` : _from;
  }

  // ── Request builders ───────────────────────────────────────────────────

  private buildUpdateViewModel(): ValveViewModel {
    const v = this.form.getRawValue();
    // Clone the entire source valve to preserve all BE-managed fields (id, components, etc.)
    const vm = ValveViewModel.fromJS(this.sourceValve!.toJSON());
    vm.tag            = v.tag ?? undefined;
    vm.businessUnits  = v.businessUnit ? [v.businessUnit] : (vm.businessUnits ?? []);
    vm.application    = v.application ?? undefined;
    vm.size           = v.sizeFrom && v.sizeTo ? `${v.sizeFrom} - ${v.sizeTo}` : (v.sizeFrom || undefined);
    vm.valveSubType   = v.subType || undefined;
    vm.shortDescription    = v.shortDescription || undefined;
    vm.pipeClassDescription = v.pipeClassDescription || undefined;
    if (v.type) {
      const ct = new ComponentType();
      ct.name = v.type;
      vm.componentType = ct;
    }
    vm.componentProperties  = this.buildRequestProperties(v.properties as any[]);
    vm.isShortDescriptionManuallyOverridden = this.isManualShortDescription;
    vm.freeFieldPropertyName  = v.freeFieldPropertyName  || undefined;
    vm.freeFieldPropertyValue = v.freeFieldPropertyValue || undefined;
    return vm;
  }


  private buildCreateRequest(): CreateValveRequest {
    const v = this.form.getRawValue();
    const req = new CreateValveRequest();
    req.tag                  = v.tag ?? undefined;
    req.businessUnit         = v.businessUnit ?? undefined;
    req.application          = v.application ?? undefined;
    req.size                 = v.sizeFrom && v.sizeTo ? `${v.sizeFrom} - ${v.sizeTo}` : (v.sizeFrom || undefined);
    req.type                 = v.type ?? undefined;
    req.subType              = v.subType || undefined;
    req.rev                  = v.rev || undefined;
    req.shortDescription     = v.shortDescription || undefined;
    req.pipeClassDescription = v.pipeClassDescription || undefined;
    req.componentProperties   = this.buildRequestProperties(v.properties as any[]);
    req.freeFieldPropertyName  = v.freeFieldPropertyName  || undefined;
    req.freeFieldPropertyValue = v.freeFieldPropertyValue || undefined;
    req.isShortDescriptionManuallyOverridden = this.isManualShortDescription;
    req.previousValve = this.mode === 'clone' ? this.sourceValveId ?? undefined : undefined;
    req.releaseId = this.releaseId ?? undefined;
    return req;
  }

  private buildRequestProperties(rows: any[]): ComponentPropertyViewModel[] {
    return rows.map(p => {
      const prop = new ComponentPropertyViewModel();
      prop.id           = p.id || undefined;
      prop.name         = this.resolvePropertyMapKey(p.name);
      prop.value        = p.value;
      prop.displayOrder = p.displayOrder ?? 0;
      prop.isActive     = true;
      return prop;
    });
  }

    /** Maps backend raw property names to their human-readable synonyms (mirrors backend _componentPropertyReadableNames) */
  private static readonly PROPERTY_NAME_SYNONYMS: Record<string, string> = {
    'subType_subType':          'type',
    'bore':                     'bore-port',
    'slips':                    'slip',
    'stem_packing':             'packing',
    'seats':                    'seat',
    'bolt_nuts':                'bolt and nuts',
    'gasket':                   'seal/gasket',
    'class_name':               'class',
    'body_bonnet':              'body/bonnet',
    'seal_gasket':              'seals/gaskets',
    'pressure_test_standard':   'pressure test standard',
    'additional_requirement':   'additional requirements'
  };

  /** Reverse map: readable name → raw name, for lookups coming from valve data */
  private static readonly PROPERTY_NAME_SYNONYMS_REVERSE: Record<string, string> =
    Object.fromEntries(
      Object.entries(ValveEditPageComponent.PROPERTY_NAME_SYNONYMS).map(([raw, readable]) => [readable, raw])
    );

  /**
   * Resolves a property name (which may be a readable synonym) to the raw key
   * used in allValvePropertyValueMap, which is keyed by the raw backend name.
   */
  private resolvePropertyMapKey(name: string): string {
    return ValveEditPageComponent.PROPERTY_NAME_SYNONYMS_REVERSE[name] ?? name;
  }
}
